// src/pages/Notifications.jsx
// Suscripciones del usuario a novedades por tipo de documento

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { subscriptionsApi } from '../services/api';
import { useTexts, TXT } from '../context/TextContext';
import CustomSelect from '../components/CustomSelect';
import { useToast } from '../context/ToastContext';

const OPT = [
  { value: 'N', labelEn: 'No',        labelEs: 'No' },
  { value: 'A', labelEn: 'Automatic', labelEs: 'Automática' },
  { value: 'E', labelEn: 'Explicit',  labelEs: 'Explícita' },
];

export default function Notifications() {
  const { t, idiId } = useTexts();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const isEn = idiId === 1;

  useEffect(() => {
    const handleFilter = (e) => navigate('/documents', { state: { filterId: e.detail?.filterId } });
    const handleSearch = (e) => navigate('/documents', { state: { search: e.detail?.text } });
    window.addEventListener('whl:filter', handleFilter);
    window.addEventListener('whl:search', handleSearch);
    return () => {
      window.removeEventListener('whl:filter', handleFilter);
      window.removeEventListener('whl:search', handleSearch);
    };
  }, [navigate]);

  const [docTypes, setDocTypes] = useState([]);
  const [subs, setSubs]         = useState([]);
  const [loading, setLoading]   = useState(true);

  const [selDocType, setSelDocType] = useState('');
  const [susData, setSusData]       = useState('N');
  const [susFile, setSusFile]       = useState('N');

  useEffect(() => {
    Promise.all([subscriptionsApi.docTypes(), subscriptionsApi.list()])
      .then(([tdoRes, subsRes]) => {
        setDocTypes(tdoRes.data);
        setSubs(subsRes.data);
        if (tdoRes.data.length) setSelDocType(String(tdoRes.data[0].id));
      })
      .catch(() => showToast(t(TXT.ERROR_LOADING_SUBS, 'Error al cargar suscripciones'), 'error'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!selDocType) return;
    if (susData === 'N' && susFile === 'N') {
      showToast(t(TXT.SELECT_ONE_OPTION, 'Seleccione al menos una opción'), 'warning');
      return;
    }
    try {
      await subscriptionsApi.save(selDocType, { susData, susFile });
      const res = await subscriptionsApi.list();
      setSubs(res.data);
      setSusData('N');
      setSusFile('N');
      showToast(t(TXT.DATA_SAVED, 'Saved'), 'success');
    } catch {
      showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  const handleRemove = async (tdoId) => {
    try {
      await subscriptionsApi.remove(tdoId);
      setSubs(prev => prev.filter(r => r.Tdo_IdTDoc !== tdoId));
    } catch {
      showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  const optLabel = (val) => {
    const o = OPT.find(x => x.value === val);
    return o ? (isEn ? o.labelEn : o.labelEs) : val;
  };

  return (
    <Layout>
      <div className="container-fluid mt-3" style={{ maxWidth: 900 }}>

        {/* ── Formulario nueva suscripción ── */}
        <div className="card mb-4">
          <div className="card-header">
            <i className="fa-solid fa-bell me-2 color1" />
            {t(TXT.NEW_SUBSCRIPTION, 'Nueva suscripción')}
          </div>
          <div className="card-body">

            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label">{t(TXT.DOC_TYPE_LABEL2, 'Tipo de documento')}:</label>
              <div className="col-sm-9">
                <CustomSelect value={selDocType} onChange={e => setSelDocType(e.target.value)}>
                  {docTypes.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </CustomSelect>
              </div>
            </div>

            <div className="row mb-3">
              <label className="col-sm-3 col-form-label">{t(TXT.NOTIFY_WHEN, 'Notificar cuando')}:</label>
              <div className="col-sm-9">
                <table className="table table-sm table-bordered mb-0" style={{ width: 'auto' }}>
                  <thead>
                    <tr>
                      <th></th>
                      <th className="text-center px-3">{isEn ? OPT[1].labelEn : OPT[1].labelEs}</th>
                      <th className="text-center px-3">{isEn ? OPT[2].labelEn : OPT[2].labelEs}</th>
                      <th className="text-center px-3">{OPT[0].labelEs}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="pe-3">{t(TXT.DATA_UPDATED_SUB, 'Datos actualizados')}</td>
                      {['A','E','N'].map(v => (
                        <td key={v} className="text-center">
                          <input type="radio" name="susData" value={v} checked={susData === v} onChange={() => setSusData(v)} />
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="pe-3">{t(TXT.FILES_UPDATED, 'Archivos actualizados')}</td>
                      {['A','E','N'].map(v => (
                        <td key={v} className="text-center">
                          <input type="radio" name="susFile" value={v} checked={susFile === v} onChange={() => setSusFile(v)} />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-9 offset-sm-3">
                <button className="button color1" onClick={handleSave}>
                  <i className="fa-solid fa-check fa-1x color1" />
                  &nbsp;{t(TXT.SAVE, 'Save')}
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* ── Lista de suscripciones existentes ── */}
        {loading ? (
          <div className="text-center mt-4"><div className="spinner-border text-secondary" /></div>
        ) : subs.length === 0 ? (
          <p className="text-muted fst-italic">{t(TXT.NO_SUBSCRIPTIONS, 'No hay suscripciones configuradas.')}</p>
        ) : (
          subs.map(r => (
            <div key={r.Tdo_IdTDoc} className="card mb-3">
              <div className="card-header fw-500 d-flex align-items-center justify-content-between">
                <span>
                  <i className="fa-solid fa-file-lines me-2 color1" />
                  {r.Tdo_NombTDoc}
                </span>
                <i className="fa-solid fa-trash text-danger" style={{ cursor: 'pointer' }}
                  onClick={() => handleRemove(r.Tdo_IdTDoc)} />
              </div>
              <div className="card-body py-2">
                <span className="me-3">
                  <span className="text-muted me-1">{t(TXT.DATA_LABEL, 'Datos:')}</span>
                  {optLabel(r.Sus_Data)}
                </span>
                <span>
                  <span className="text-muted me-1">{t(TXT.FILES_LABEL, 'Archivos:')}</span>
                  {optLabel(r.Sus_File)}
                </span>
              </div>
            </div>
          ))
        )}

      </div>
    </Layout>
  );
}
